import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useServices } from '@/contexts/app-services';
import type { Plan, PlanCreate, PlanUpdate } from '@/entities/plan';
import {
  buildPlanningStatuses,
  preparePlanCreate,
} from '@/entities/plan/operations';
import type { HigherPeriod, Period } from '@/entities/types';
import { logError } from '@/services/logger';

export const planKeys = {
  byPeriod: (period: string, startAtLocal: string) =>
    ['plans', period, startAtLocal] as const,
  planningStatuses: (...checksKey: string[]) =>
    ['planning-statuses', ...checksKey] as const,
};

const pendingCreations = new Set<string>();
const POSTGRES_UNIQUE_VIOLATION = '23505';

interface PlansApi {
  readPlanForPeriod: (
    period: Period,
    startAtLocal: string,
  ) => Promise<Plan | null>;
  createPlan: (planCreate: PlanCreate) => Promise<Plan>;
}

function isUniqueViolation(err: unknown): boolean {
  return (
    err !== null &&
    typeof err === 'object' &&
    'code' in err &&
    (err as { code: unknown }).code === POSTGRES_UNIQUE_VIOLATION
  );
}

export async function getOrCreatePlan(
  plansApi: PlansApi,
  period: Period,
  startAt: Date,
  startAtLocal: string,
): Promise<Plan> {
  const existing = await plansApi.readPlanForPeriod(period, startAtLocal);
  if (existing) {
    return existing;
  }

  try {
    return await plansApi.createPlan(
      preparePlanCreate({ period, startAt, startAtLocal }),
    );
  } catch (err) {
    if (isUniqueViolation(err)) {
      const reread = await plansApi.readPlanForPeriod(period, startAtLocal);
      if (!reread) {
        throw new Error('Plan unavailable after 23505 unique violation', {
          cause: err,
        });
      }
      return reread;
    }
    throw err;
  }
}

export async function ensurePlanCached(
  queryClient: QueryClient,
  plansApi: PlansApi,
  period: Period,
  startAt: Date,
  startAtLocal: string,
): Promise<Plan> {
  const plan = await getOrCreatePlan(plansApi, period, startAt, startAtLocal);
  queryClient.setQueryData(planKeys.byPeriod(period, startAtLocal), plan);
  return plan;
}

export function usePlanForPeriod(period: Period, startAtLocal: string) {
  const { api } = useServices();

  return useQuery({
    queryKey: planKeys.byPeriod(period, startAtLocal),
    queryFn: () => api.plans.readPlanForPeriod(period, startAtLocal),
  });
}

export function useOrCreatePlan(
  period: Period,
  startAt: Date,
  startAtLocal: string,
) {
  const queryClient = useQueryClient();
  const { api } = useServices();

  const {
    isLoading: planLoading,
    isError: planError,
    data: plan,
    error: planQueryError,
  } = usePlanForPeriod(period, startAtLocal);

  const {
    isPending: createPending,
    mutate: createMutate,
    error: createError,
  } = useMutation({
    mutationFn: () => getOrCreatePlan(api.plans, period, startAt, startAtLocal),
    onSuccess: (data) => {
      queryClient.setQueryData(planKeys.byPeriod(period, startAtLocal), data);
    },
    onError: (error) => {
      logError(error, 'Failed to create plan');
    },
  });

  const isLoading = planLoading || createPending;
  const creationKey = `${period}-${startAtLocal}`;

  // Tracks the key this instance locked so unmount can release it.
  const lockedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      !plan &&
      !isLoading &&
      !planError &&
      !createError &&
      !pendingCreations.has(creationKey)
    ) {
      pendingCreations.add(creationKey);
      lockedKeyRef.current = creationKey;
      createMutate(undefined, {
        onSettled: () => {
          pendingCreations.delete(creationKey);
          lockedKeyRef.current = null;
        },
      });
    }
  }, [plan, isLoading, planError, createError, creationKey, createMutate]);

  // Release the lock if this instance unmounts while a create is in flight.
  useEffect(() => {
    return () => {
      const key = lockedKeyRef.current;
      if (key !== null) {
        pendingCreations.delete(key);
      }
    };
  }, []);

  return {
    plan,
    isLoading,
    error: plan ? null : (planQueryError ?? createError),
  };
}

interface UpdatePlanArgs {
  planId: string;
  period: Period;
  startAtLocal: string;
  update: PlanUpdate;
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();
  const { api, analytics } = useServices();

  return useMutation({
    mutationFn: async ({ planId, update }: UpdatePlanArgs) => {
      return api.plans.updatePlan(planId, update);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        planKeys.byPeriod(variables.period, variables.startAtLocal),
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: planKeys.byPeriod(variables.period, variables.startAtLocal),
      });
      if (variables.update.status) {
        analytics.trackUpdatedPlan({
          planPeriod: variables.period,
          planStartAtLocal: variables.startAtLocal,
          field: 'status',
          previousValue: 'created',
          value: variables.update.status,
        });
      }
    },
  });
}

export function usePlanningStatuses(
  checks: Array<{ period: HigherPeriod; startAtLocal: string }>,
) {
  const { api } = useServices();

  const checksKey = checks.map((c) => `${c.period}:${c.startAtLocal}`);

  const {
    data: plannedPeriods,
    isLoading,
    isError,
  } = useQuery({
    queryKey: planKeys.planningStatuses(...checksKey),
    queryFn: () => api.plans.readPlannedPlansByPeriods(checks),
  });

  const data = plannedPeriods
    ? buildPlanningStatuses(plannedPeriods, checks)
    : undefined;

  return { data, isLoading, isError };
}
